import {
  Button,
  Checkbox,
  Divider,
  Group,
  Menu,
  Stack,
  Text
} from "@mantine/core";
import { useState } from "react";
import "./FilterDropdown.scss";
import type {
  FilterDropdownFilter,
  FilterDropdownFilterGroup,
  FilterDropdownFilters,
  FilterDropdownValues
} from "./FilterDropdown.types";
import { NumberFilterItem } from "./FilterItems/NumberFilterItem";
import { RadioFilterItem } from "./FilterItems/RadioFilterItem";

interface FilterDropdownProps {
  defaultValues: FilterDropdownValues;
  filters: FilterDropdownFilters;
  values: FilterDropdownValues;
  onSave: (newValues: FilterDropdownValues) => void;
}

function FilterDropdown({
  defaultValues,
  filters,
  values,
  onSave
}: FilterDropdownProps) {
  const [localSelectedFilters, setLocalSelectedFilters] =
    useState<FilterDropdownValues>(values);
  const [isOpen, setIsOpen] = useState(false);

  function handleFilterChange(
    key: string,
    value: FilterDropdownValues[string]
  ) {
    setLocalSelectedFilters((oldFilters) => ({
      ...oldFilters,
      [key]: value
    }));
  }

  function handleClickReset() {
    setLocalSelectedFilters(defaultValues);
    setIsOpen(false);
    onSave(defaultValues);
  }

  function handleClickSave() {
    onSave(localSelectedFilters);
    setIsOpen(false);
  }

  const filteredValuesCount = Object.values(values).filter(Boolean).length;

  return (
    <Menu
      classNames={{ dropdown: "FilterDropdownMenu" }}
      opened={isOpen}
      onChange={setIsOpen}
    >
      <Menu.Target>
        <Button variant="default">
          Filter
          {filteredValuesCount > 0 && (
            <Text c="primary" ml="sm">
              {filteredValuesCount}
            </Text>
          )}
        </Button>
      </Menu.Target>
      <Menu.Dropdown mah={"calc(100vh - 200px)"} miw={300} p="md">
        {filters.map((filter) => (
          <FilterRenderer
            filter={filter}
            key={filter.key}
            values={localSelectedFilters}
            onChange={handleFilterChange}
          />
        ))}

        <Divider mb="md" />
        <Group justify="space-between">
          <Button variant="default" onClick={handleClickReset}>
            Löschen
          </Button>
          <Button onClick={handleClickSave}>Anwenden</Button>
        </Group>
      </Menu.Dropdown>
    </Menu>
  );
}

interface FilterRendererProps {
  filter: FilterDropdownFilterGroup | FilterDropdownFilter;
  values: FilterDropdownValues;
  onChange: (key: string, value: FilterDropdownValues[string]) => void;
}

function FilterRenderer({ filter, values, onChange }: FilterRendererProps) {
  if ("header" in filter) {
    return (
      <Stack gap="xs" mb="md">
        <Text fw={500}>{filter.header}</Text>
        {filter.filters.map((filter) => (
          <FilterRenderer
            filter={filter}
            key={filter.key}
            values={values}
            onChange={onChange}
          />
        ))}
      </Stack>
    );
  }

  switch (filter.type) {
    case "boolean":
      return (
        <Checkbox
          checked={Boolean(values[filter.key])}
          label={filter.label}
          onChange={() => onChange(filter.key, !values[filter.key])}
        />
      );
    case "number":
      return (
        <NumberFilterItem
          filterValue={
            values[filter.key] !== undefined
              ? Number(values[filter.key])
              : undefined
          }
          key={filter.key}
          label={filter.label}
          max={filter.max}
          min={filter.min}
          rightSection={filter.inputGroupText}
          onChange={(value) => onChange(filter.key, value)}
        />
      );
    case "radio":
      return (
        <RadioFilterItem
          choices={filter.choices}
          filterValue={String(values[filter.key])}
          key={filter.key}
          radioName={filter.radioName}
          onChange={(value) => onChange(filter.key, value)}
        />
      );
    default:
      console.error("Filter type not implemented for filter: ", filter);
      return null;
  }
}

export { FilterDropdown, FilterDropdownProps };
